import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Avatar,
  useMediaQuery,
  useTheme as useMuiTheme,
  alpha,
  Fade,
  Tooltip,
} from '@mui/material';
import { Explore, Groups, Settings } from '@mui/icons-material'; 
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

interface CommunityMobileBarProps {
  activeView: 'posts' | 'communities' | 'stats';
  setActiveView: (view: 'posts' | 'communities' | 'stats') => void;
  isDark: boolean;
  goldColor?: string;
  deepGreen?: string;
  handleCreatePost: () => void;
  handleCreateCommunity: () => void;
}

const CommunityMobileBar: React.FC<CommunityMobileBarProps> = ({
  activeView,
  setActiveView,
  isDark,
  goldColor = '#C4B66F',
  deepGreen = '#051814',
  handleCreatePost,
  handleCreateCommunity,
}) => {
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showCreateOverlay, setShowCreateOverlay] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const createButtonRef = useRef<HTMLDivElement>(null);

  if (!isMobile) return null;

  const glassEffect = {
    background: isDark 
      ? 'rgba(0, 0, 0, 0.85)' 
      : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    border: isDark 
      ? '1px solid rgba(196, 182, 111, 0.15)' 
      : '1px solid rgba(5, 24, 20, 0.08)',
    boxShadow: isDark 
      ? '0 4px 24px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.05)' 
      : '0 4px 24px rgba(5, 24, 20, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.9)',
  };

  const actionButtonsGlassEffect = {
    background: isDark 
      ? 'rgba(0, 0, 0, 0.95)' 
      : 'rgba(255, 255, 255, 0.98)',
    backdropFilter: 'blur(25px) saturate(200%)',
    WebkitBackdropFilter: 'blur(25px) saturate(200%)',
    border: isDark 
      ? '1px solid rgba(196, 182, 111, 0.25)' 
      : '1px solid rgba(5, 24, 20, 0.15)',
    boxShadow: isDark 
      ? '0 8px 32px rgba(0, 0, 0, 0.7), inset 0 1px 0 rgba(255, 255, 255, 0.1)' 
      : '0 8px 32px rgba(5, 24, 20, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.95)',
  };

  const getButtonStyle = (view: 'posts' | 'communities' | 'stats' | 'profile') => ({
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px',
    borderRadius: '16px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative' as const,
    '&:hover': {
      background: isDark 
        ? `linear-gradient(135deg, ${alpha(goldColor, 0.1)} 0%, ${alpha(goldColor, 0.05)} 100%)` 
        : `linear-gradient(135deg, ${alpha(deepGreen, 0.08)} 0%, ${alpha(deepGreen, 0.03)} 100%)`,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: '-2px',
      left: '50%',
      transform: activeView === view ? 'translateX(-50%) scaleX(1)' : 'translateX(-50%) scaleX(0)',
      width: '60%',
      height: '3px',
      background: isDark 
        ? `linear-gradient(90deg, transparent, ${goldColor}, transparent)`
        : `linear-gradient(90deg, transparent, ${deepGreen}, transparent)`,
      borderRadius: '2px',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      boxShadow: activeView === view 
        ? (isDark 
            ? `0 0 8px ${alpha(goldColor, 0.8)}, 0 0 12px ${alpha(goldColor, 0.4)}`
            : `0 0 8px ${alpha(deepGreen, 0.6)}, 0 0 12px ${alpha(deepGreen, 0.3)}`)
        : 'none',
    },
  });

  const handleProfileClick = () => {
    if (user) {
      navigate('/profile');
    } else {
      navigate('/login');
    }
  };

  // Close overlay when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showCreateOverlay &&
        overlayRef.current &&
        createButtonRef.current &&
        !overlayRef.current.contains(event.target as Node) &&
        !createButtonRef.current.contains(event.target as Node)
      ) {
        setShowCreateOverlay(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCreateOverlay]);

  const handleCreatePostClick = () => {
    handleCreatePost();
    setShowCreateOverlay(false);
  };

  const handleCreateCommunityClick = () => {
    handleCreateCommunity();
    setShowCreateOverlay(false);
  };

  const handleCreateButtonClick = () => {
    setShowCreateOverlay(!showCreateOverlay);
  };

  return (
    <>
      {/* Action Buttons Overlay (appears on click of + icon) */}
      <Fade in={showCreateOverlay} timeout={200}>
        <Box 
          ref={overlayRef}
          sx={{
            position: 'fixed',
            bottom: 85,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100%',
            maxWidth: '200px',
            zIndex: 999,
            pointerEvents: showCreateOverlay ? 'auto' : 'none',
          }}
        >
          <Box sx={{
            ...actionButtonsGlassEffect,
            borderRadius: '20px',
            padding: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}>
            {/* Create Post Button */}
            <Box 
              sx={{
                display: 'flex',
                alignItems: 'center',
                padding: '5px 5px',
                borderRadius: '20px',
                cursor: 'pointer',
                background: isDark 
                  ? `linear-gradient(135deg, ${alpha(goldColor, 0.2)} 0%, ${alpha(goldColor, 0.1)} 100%)` 
                  : `linear-gradient(135deg, ${alpha(deepGreen, 0.1)} 0%, ${alpha(deepGreen, 0.05)} 100%)`,
                border: isDark 
                  ? `1px solid ${alpha(goldColor, 0.2)}`
                  : `1px solid ${alpha(deepGreen, 0.1)}`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: isDark 
                    ? `linear-gradient(135deg, ${alpha(goldColor, 0.25)} 0%, ${alpha(goldColor, 0.15)} 100%)` 
                    : `linear-gradient(135deg, ${alpha(deepGreen, 0.15)} 0%, ${alpha(deepGreen, 0.08)} 100%)`,
                  transform: 'translateY(-1px)',
                }
              }}
              onClick={handleCreatePostClick}
            >
              <Box sx={{ 
                width: 24, 
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path 
                    d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z" 
                    fill={isDark ? goldColor : deepGreen}
                  />
                </svg>
              </Box>
              <Typography sx={{
                fontSize: '0.85rem',
                fontWeight: 600,
                color: isDark ? goldColor : deepGreen,
              }}>
                Create Post
              </Typography>
            </Box>

            {/* Create Community Button */}
            <Box 
              sx={{
                display: 'flex',
                alignItems: 'center',
                padding: '5px 5px',
                borderRadius: '20px',
                cursor: 'pointer',
                background: isDark 
                  ? `linear-gradient(135deg, ${alpha(goldColor, 0.2)} 0%, ${alpha(goldColor, 0.1)} 100%)` 
                  : `linear-gradient(135deg, ${alpha(deepGreen, 0.1)} 0%, ${alpha(deepGreen, 0.05)} 100%)`,
                border: isDark 
                  ? `1px solid ${alpha(goldColor, 0.2)}`
                  : `1px solid ${alpha(deepGreen, 0.1)}`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: isDark 
                    ? `linear-gradient(135deg, ${alpha(goldColor, 0.25)} 0%, ${alpha(goldColor, 0.15)} 100%)` 
                    : `linear-gradient(135deg, ${alpha(deepGreen, 0.15)} 0%, ${alpha(deepGreen, 0.08)} 100%)`,
                  transform: 'translateY(-1px)',
                }
              }}
              onClick={handleCreateCommunityClick}
            >
              <Box sx={{ 
                width: 24, 
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path 
                    d="M16 8C16 10.21 14.21 12 12 12C9.79 12 8 10.21 8 8C8 5.79 9.79 4 12 4C14.21 4 16 5.79 16 8ZM12 14C16.42 14 20 15.79 20 18V20H4V18C4 15.79 7.58 14 12 14Z" 
                    fill={isDark ? goldColor : deepGreen}
                  />
                </svg>
              </Box>
              <Typography sx={{
                fontSize: '0.85rem',
                fontWeight: 600,
                color: isDark ? goldColor : deepGreen,
              }}>
                Create Community
              </Typography>
            </Box>
          </Box>
        </Box>
      </Fade>

      {/* Main Mobile Navigation Bar */}
      <Box sx={{
        position: 'fixed',
        bottom: 12,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'calc(100vw - 24px)',
        maxWidth: '400px',
        zIndex: 1000,
      }}>
        <Box sx={{
          ...glassEffect,
          borderRadius: '28px',
          padding: '8px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          {/* Communities Button */}
          <Tooltip title="Communities" placement="top" arrow enterDelay={300}>
            <Box 
              sx={getButtonStyle('communities')}
              onClick={() => setActiveView('communities')}
            >
              <Box sx={{ 
                width: 20, 
                height: 28,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Groups sx={{
                  width: 30, 
                  height: 30,
                  color: activeView === 'communities' 
                    ? (isDark ? goldColor : deepGreen) 
                    : (isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)')
                }} />
              </Box>
            </Box>
          </Tooltip>

          {/* Posts Button */}
          <Tooltip title="Posts" placement="top" arrow enterDelay={300}>
            <Box 
              sx={getButtonStyle('posts')}
              onClick={() => setActiveView('posts')}
            >
              <Box sx={{ 
                width: 28, 
                height: 28,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Explore sx={{
                  width: 30, 
                  height: 30,
                  color: activeView === 'posts' 
                    ? (isDark ? goldColor : deepGreen) 
                    : (isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)')
                }}/>
              </Box>
            </Box>
          </Tooltip>

          {/* Circular + Icon in Center */}
          <Tooltip title="Create" placement="top" arrow enterDelay={300}>
            <Box 
              ref={createButtonRef}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '52px',
                height: '52px',
                borderRadius: '50%',
                background: isDark 
                  ? `linear-gradient(135deg, ${alpha(goldColor, 0.35)} 0%, ${alpha(goldColor, 0.25)} 100%)` 
                  : `linear-gradient(135deg, ${alpha(deepGreen, 0.25)} 0%, ${alpha(deepGreen, 0.15)} 100%)`,
                border: isDark
                  ? `2px solid ${alpha(goldColor, 0.4)}`
                  : `2px solid ${alpha(deepGreen, 0.3)}`,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: isDark 
                  ? '0 4px 15px rgba(196, 182, 111, 0.3)' 
                  : '0 4px 15px rgba(5, 24, 20, 0.2)',
                '&:hover': {
                  background: isDark 
                    ? `linear-gradient(135deg, ${alpha(goldColor, 0.4)} 0%, ${alpha(goldColor, 0.3)} 100%)` 
                    : `linear-gradient(135deg, ${alpha(deepGreen, 0.3)} 0%, ${alpha(deepGreen, 0.2)} 100%)`,
                  transform: 'scale(1.1)',
                  boxShadow: isDark 
                    ? '0 6px 20px rgba(196, 182, 111, 0.4)' 
                    : '0 6px 20px rgba(5, 24, 20, 0.3)',
                }
              }}
              onClick={handleCreateButtonClick}
            >
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
                <path 
                  d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z" 
                  fill={isDark ? '#000' : '#fff'}
                />
              </svg>
            </Box>
          </Tooltip>

          {/* Stats Button */}
          <Tooltip title="Stats" placement="top" arrow enterDelay={300}>
            <Box 
              sx={getButtonStyle('stats')}
              onClick={() => setActiveView('stats')}
            >
              <Box sx={{ 
                width: 28, 
                height: 28,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Settings sx={{
                  width: 30, 
                  height: 30,
                  color: activeView === 'stats' 
                    ? (isDark ? goldColor : deepGreen) 
                    : (isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)')
                }}/>
              </Box>
            </Box>
          </Tooltip>

          {/* Profile/Login Button */}
          <Tooltip title={user ? "Profile" : "Login"} placement="top" arrow enterDelay={300}>
            <Box 
              sx={getButtonStyle('profile')}
              onClick={handleProfileClick}
            >
              {user ? (
                <Avatar
                  src={user.profilePicture || ''}
                  sx={{
                    width: 35,
                    height: 35,
                  }}
                />
              ) : (
                <Box sx={{ 
                  width: 28, 
                  height: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
                    <path 
                      d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" 
                      fill={activeView === 'profile' 
                        ? (isDark ? goldColor : deepGreen) 
                        : (isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)')}
                    />
                  </svg>
                </Box>
              )}
            </Box>
          </Tooltip>
        </Box>
      </Box>
    </>
  );
};

export default CommunityMobileBar;